/**
 * O menu, num lugar só — por PAPEL, não por tela.
 *
 * O que isto substitui: até 12/08 cada tela montava o próprio cabeçalho de
 * navegação. O console mandava pra "Turmas" e "Escola" (`ClassConsole.tsx:384`
 * e `:391`), o painel da escola mandava pra "Equipe" e "Turmas", e
 * `/admin/turmas` e `/admin/escola` não mandavam pra lugar nenhum — só a seta
 * de voltar. Três listas de destinos escritas em três arquivos, divergindo
 * sozinhas a cada tela nova.
 *
 * A regra de quem vê o quê é a MATRIZ DE PERMISSÕES do
 * `.planning/PAPEIS-E-ESCOLAS.md`, não uma lista paralela — se um item aparece
 * aqui pra um papel que a RLS nega, o usuário clica e toma "Esta área não é
 * sua". Menu que oferece porta fechada é pior que menu curto.
 *
 * ⚠️ ISTO NÃO É AUTORIZAÇÃO. Quem autoriza é a RLS e as Edge Functions; o gate
 * de rota (`ProtectedRoute minimo=`) é a segunda camada. Este arquivo decide só
 * o que se OFERECE — some o que a pessoa não pode usar, pra ela não descobrir o
 * limite batendo nele.
 *
 * Registro: product (DESIGN.md). O menu é mobília do console — some dentro da
 * tarefa. Nada de adesivo aqui, e teal só no item ativo.
 */
import {
  LayoutDashboard,
  PlayCircle,
  Building2,
  GraduationCap,
  UsersRound,
  UserRoundCheck,
  BookOpen,
  BarChart3,
  ScrollText,
  DoorOpen,
  Clapperboard,
  type LucideIcon,
} from 'lucide-react';
import { alcanca, type Papel } from '@/hooks/usePapel';
import { MARCAS_COM_MENU_LATERAL } from '@/branding/identidade';
import { FIXED_BRAND_ID } from '@/branding/product';

/**
 * Esta marca navega por menu lateral?
 *
 * Mora AQUI, e não no componente da casca, por causa do ciclo de import: a
 * casca monta o menu, o menu monta a conta, e a conta precisa saber se a casca
 * existe pra não desenhar um segundo avatar. Módulo neutro quebra o laço.
 */
export const TEM_MENU_LATERAL = MARCAS_COM_MENU_LATERAL.has(FIXED_BRAND_ID ?? '');

export interface ItemDeMenu {
  rotulo: string;
  para: string;
  icone: LucideIcon;
  /**
   * `true` = o item também fica aceso nas sub-rotas (`/console` acende em
   * `/console/:id`). `false` = só no caminho exato — é o caso de `/admin`, que
   * senão acenderia junto com `/admin/turmas`, que é de outro grupo.
   */
  prefixo: boolean;
  /** Uma linha, pro estado vazio e pro título do documento. Não vai na tela. */
  descricao: string;
  /**
   * Cor do ÍCONE — só o menu do ALUNO usa (pedido do Matheus, 19/08: itens
   * maiores e mais chamativos no registro lúdico). Staff fica sem cor de
   * propósito: o menu de quem trabalha é mobília e não disputa com a tarefa.
   */
  cor?: string;
}

export interface GrupoDeMenu {
  /** `null` = itens soltos no topo, sem cabeçalho de grupo. */
  titulo: string | null;
  itens: ItemDeMenu[];
}

/** A casa de cada papel quando a marca tem menu lateral. */
export const PAINEL = '/painel';

const PAINEL_ITEM: ItemDeMenu = {
  rotulo: 'Painel',
  para: PAINEL,
  icone: LayoutDashboard,
  prefixo: false,
  descricao: 'O resumo do seu dia',
};

/**
 * O menu do ALUNO.
 *
 * Curto de propósito: no NEXTGEN o produto do aluno é a aula ao vivo, e fora
 * dela ele tem a casa (`/minhas-aulas`) e a porta de entrada (`/entrar`). A
 * casa dele NÃO é o `/painel` — ela já é o painel dele, em registro lúdico,
 * com histórico, missões e quiz (decisão de 07/08).
 */
function menuDoAluno(): GrupoDeMenu[] {
  return [
    {
      titulo: null,
      itens: [
        {
          rotulo: 'Minhas aulas',
          para: '/minhas-aulas',
          icone: LayoutDashboard,
          prefixo: true,
          descricao: 'As aulas que você fez, pra reassistir',
          cor: '#4cc9f0',
        },
        {
          rotulo: 'Recursos',
          para: '/recursos',
          icone: BookOpen,
          prefixo: false,
          descricao: 'Vídeos e materiais extras de cada aula',
          cor: '#a78bfa',
        },
        {
          rotulo: 'Entrar na sala',
          para: '/entrar',
          icone: DoorOpen,
          prefixo: false,
          descricao: 'Digite o código que está no telão',
          cor: '#2ee6a8',
        },
      ],
    },
  ];
}

/**
 * O menu de quem trabalha na escola.
 *
 * Ordem por FREQUÊNCIA de uso, não por hierarquia de poder: quem abre esta tela
 * na segunda de manhã vai dar aula, não conferir cadastro.
 */
export function menuDoPapel(papel: Papel | null | undefined): GrupoDeMenu[] {
  if (!alcanca(papel, 'professor')) return menuDoAluno();

  const grupos: GrupoDeMenu[] = [
    { titulo: null, itens: [PAINEL_ITEM] },
    {
      titulo: 'Aula',
      itens: [
        {
          /* "Modo Classe" desde 12/08 (José). "Começar aula" descrevia o CLIQUE;
             o nome do produto é este, e é como o time já fala dele — o próprio
             título da tela e a spec sempre disseram MODO CLASSE. */
          rotulo: 'Modo Classe',
          para: '/console',
          icone: PlayCircle,
          prefixo: true,
          descricao: 'Escolha a turma e abra a sala',
        },
      ],
    },
  ];

  /* ALUNOS é do professor também, desde 13/08: é onde mora a fila de matrículas
     esperando liberação, e quem sabe se o aluno novo é da sala é quem está na
     sala. A RLS recorta sozinha — `is_class_teacher` entrega ao professor só o
     roster das turmas dele, e à coordenação o da escola inteira. Continua sendo
     socorro, não gestão: mover de turma e remover seguem da coordenação. */
  if (alcanca(papel, 'professor') && !alcanca(papel, 'coordenador')) {
    grupos.push({
      titulo: 'Escola',
      itens: [
        /* TURMAS entrou pro professor em 13/08 (José). Ele vê SÓ as dele — a
           policy "Teachers read their own classes" recorta — e a tela é de
           LEITURA pra ele: sem criar, sem editar, sem provisionar, porque as
           policies de escrita são todas de coordenador pra cima. O que ele vem
           buscar aqui é o CÓDIGO da turma, que ele dita pro aluno se cadastrar,
           e o roster com o botão de nova senha. */
        {
          rotulo: 'Turmas',
          para: '/admin/turmas',
          icone: GraduationCap,
          prefixo: false,
          descricao: 'Suas turmas, o código e o roster',
        },
        {
          rotulo: 'Alunos',
          para: '/admin/alunos',
          icone: UserRoundCheck,
          prefixo: false,
          descricao: 'Liberar matrícula dos alunos das suas turmas',
        },
      ],
    });
  }

  // Gestão é da COORDENAÇÃO (decisão de 06/08). O professor rege a aula e
  // socorre em sala — nova senha e liberar aparelho ficam DENTRO da sessão,
  // onde ele precisa delas, e não num cadastro que ele não administra.
  if (alcanca(papel, 'coordenador')) {
    grupos.push({
      titulo: 'Escola',
      itens: [
        {
          rotulo: 'Painel da escola',
          para: '/escola',
          icone: Building2,
          prefixo: false,
          descricao: 'Onde cada turma parou',
        },
        /* SEPARADOS em 12/08, a pedido do José. Eram um item só ("Turmas e
           alunos") porque a tela era uma só — e era a tela que forçava isso: pra
           ver um aluno você escolhia a turma dele primeiro. Ninguém conseguia
           responder "quantos alunos a escola tem" sem somar turma por turma.

           Provisionar EM LOTE continua na turma, e não aqui: a folha de senhas
           provisórias é impressa por turma, que é como ela é entregue em sala. */
        {
          rotulo: 'Turmas',
          para: '/admin/turmas',
          icone: GraduationCap,
          prefixo: false,
          descricao: 'Abrir turma, trocar professor, ver o roster',
        },
        {
          rotulo: 'Alunos',
          para: '/admin/alunos',
          icone: UserRoundCheck,
          prefixo: false,
          descricao: 'Todos os alunos da escola, em uma lista',
        },
        {
          rotulo: 'Equipe',
          para: '/admin/escola',
          icone: UsersRound,
          prefixo: false,
          descricao: 'Diretor, coordenador e professor',
        },
      ],
    });
  }

  // Curso, aula e liberação são do superadmin — é a 12Brain que produz o
  // conteúdo, não a escola cliente (matriz do PAPEIS-E-ESCOLAS.md).
  if (alcanca(papel, 'superadmin')) {
    grupos.push({
      titulo: 'Conteúdo',
      itens: [
        {
          rotulo: 'Cursos e aulas',
          para: '/admin',
          icone: BookOpen,
          prefixo: false,
          descricao: 'Cadastro de curso, aula e liberação',
        },
        {
          rotulo: 'Análises',
          para: '/admin/analytics',
          icone: BarChart3,
          prefixo: false,
          descricao: 'Uso e desempenho por aula',
        },
        {
          rotulo: 'Registros técnicos',
          para: '/admin/logs',
          icone: ScrollText,
          prefixo: false,
          descricao: 'Erros e eventos do sistema',
        },
      ],
    });
  }

  return grupos;
}

/**
 * O item está aceso?
 *
 * `prefixo` existe porque `/admin` é pai de `/admin/turmas` na URL mas não no
 * menu — os dois moram em grupos diferentes, e acender os dois juntos diria ao
 * professor que ele está em dois lugares ao mesmo tempo.
 */
export function itemAtivo(caminho: string, item: ItemDeMenu): boolean {
  if (caminho === item.para) return true;
  return item.prefixo && caminho.startsWith(`${item.para}/`);
}

/** Achatado, pra quem precisa da lista sem os grupos (breadcrumb, título). */
export function itensDoPapel(papel: Papel | null | undefined): ItemDeMenu[] {
  return menuDoPapel(papel).flatMap((g) => g.itens);
}

/** O item que corresponde à tela aberta — para o título da barra no celular. */
export function itemDoCaminho(
  papel: Papel | null | undefined,
  caminho: string,
): ItemDeMenu | null {
  const exato = itensDoPapel(papel).find((i) => i.para === caminho);
  if (exato) return exato;
  return itensDoPapel(papel).find((i) => itemAtivo(caminho, i)) ?? null;
}
